import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  Ref,
} from "react";
import SearchIcon from "@mui/icons-material/Search";
import BackspaceIcon from "@mui/icons-material/Backspace";
import { toast } from "react-toastify";
import { api } from "../../api/apiClient";
import "../../css/forms.css";
import "../../css/buttons.css";
import "./AddressLookUp.css";

interface AddressSearchProps {
  sendAddressToParent: (data: any) => void;
}

interface Address {
  addressId: number;
  fullAddress: string;
}

const AddressLookUp = forwardRef(
  (props: AddressSearchProps, ref: Ref<any>) => {
    const [postcode, setPostcode] = useState("");
    const [addresses, setAddresses] = useState<Address[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);

    useImperativeHandle(ref, () => ({
      clearFromParent,
    }));

    /* =========================
       Fetch Addresses
       ========================= */

    const fetchAddresses = async () => {
      if (!postcode.trim()) {
        toast.error("Postcode is required.");
        return;
      }

      const sanitizedPostCode = postcode
        .replace(/\s+/g, "")
        .toUpperCase();

      try {
        setLoading(true);

        const addressList = await api<Address[]>(
          `/api/address/GetAddressesForPostCode/${sanitizedPostCode}`,
          { method: "GET" }
        );

        const results = Array.isArray(addressList)
          ? addressList
          : [];

        if (!results.length) {
          toast.error("No addresses found.");
          setShowDropdown(false);
        } else {
          setAddresses(results);
          setShowDropdown(true);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to fetch addresses.");
      } finally {
        setLoading(false);
      }
    };

    /* =========================
       Handlers
       ========================= */

    const handleInputChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      setPostcode(e.target.value);
      setShowDropdown(false);
    };

    const handleKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (e.key === "Enter") {
        e.preventDefault();
        fetchAddresses();
      }
    };

    const handleSelectChange = (
      e: React.ChangeEvent<HTMLSelectElement>
    ) => {
      const selectedAddressId = e.target.value;
      if (!selectedAddressId) return;

      const selectedAddress = addresses.find(
        (a) =>
          a.addressId.toString() === selectedAddressId
      );

      if (selectedAddress) {
        props.sendAddressToParent(selectedAddress);
      }
    };

    /* =========================
       FULL RESET
       ========================= */

    const reset = () => {
      setPostcode("");
      setAddresses([]);
      setShowDropdown(false);

      // Clear parent address fields
      props.sendAddressToParent({
        addressLine1: "",
        postTown: "",
        county: "",
        postCode: "",
        fullAddress: "",
      });
    };

    const clearFromParent = () => {
      reset();
    };

    /* =========================
       Render
       ========================= */

    return (
      <div className="search-container address-lookup-section">

        {!showDropdown ? (
          /* =========================
             SEARCH MODE
             ========================= */
          <div>
            <div className="address-lookup-search-row">
            <input
              type="text"
              placeholder="Find by postcode, or enter the address manually below"
              className="form-input"
              value={postcode}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={loading}
              aria-label="Postcode"
            />
            <button
              type="button"
              className="action-button primary address-lookup-search-button"
              onClick={fetchAddresses}
              disabled={loading || !postcode.trim()}
            >
              <SearchIcon fontSize="small" />
              {loading ? "Finding..." : "Find address"}
            </button>
            </div>
          </div>
        ) : (
          /* =========================
             DROPDOWN MODE
             ========================= */
          <div className="search-input-wrapper">

            <BackspaceIcon
              className="search-icon-left"
              onClick={reset}
              style={{ cursor: "pointer" }}
            />

            <select
              className="form-input search-input-with-icons"
              onChange={handleSelectChange}
              defaultValue=""
            >
              <option value="">
                Select an address...
              </option>
              {addresses.map((address) => (
                <option
                  key={address.addressId}
                  value={address.addressId}
                >
                  {address.fullAddress}
                </option>
              ))}
            </select>

          </div>
        )}

      </div>
    );
  }
);

export default AddressLookUp;
